/**
 * OpenAI Ads Measurement Pixel
 * Loads only after marketing consent is granted.
 */

import { useEffect } from 'react';
import { hasMarketingConsent } from './cookieConsent';

// Public Pixel ID (Ads Manager data source "Nebenkostenprofi.de").
export const OPENAI_PIXEL_ID = 'PcmhtNG14UnZRAUd5tHN52';
const OPENAI_PIXEL_SCRIPT_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

function ensureOaiqQueue() {
  if (typeof window === 'undefined' || window.oaiq) return;
  const q = function () {
    q.q.push(arguments);
  };
  q.q = [];
  window.oaiq = q;
}

export function loadOpenAiPixel() {
  if (typeof window === 'undefined') return;
  if (!OPENAI_PIXEL_ID) return;
  if (!hasMarketingConsent()) return;

  ensureOaiqQueue();

  const existingScript = document.querySelector(`script[src="${OPENAI_PIXEL_SCRIPT_URL}"]`);
  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = OPENAI_PIXEL_SCRIPT_URL;
    document.head.appendChild(script);
  }

  window.oaiq('init', { pixelId: OPENAI_PIXEL_ID });
}

function clearFirstPartyCookie(cookieName) {
  const hostname = window.location.hostname;
  const domainParts = hostname.split('.');
  const domains = [
    hostname,
    `.${hostname}`,
    domainParts.length > 1 ? `.${domainParts.slice(-2).join('.')}` : `.${hostname}`,
  ];

  domains.forEach((domain) => {
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
  });
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export function unloadOpenAiPixel() {
  if (typeof window === 'undefined') return;

  document.querySelectorAll(`script[src="${OPENAI_PIXEL_SCRIPT_URL}"]`).forEach((script) => {
    script.remove();
  });

  clearFirstPartyCookie('__oppref');
  clearFirstPartyCookie('__obref');
  delete window.oaiq;
}

function readCookie(name) {
  if (typeof document === 'undefined') return undefined;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function getOpenAiAttributionCookies() {
  return {
    oppref: readCookie('__oppref') || undefined,
    obref: readCookie('__obref') || undefined,
  };
}

export function measureLeadCreated(eventId) {
  if (typeof window === 'undefined' || !window.oaiq || !eventId) return;
  if (!hasMarketingConsent()) return;
  window.oaiq(
    'measure',
    'lead_created',
    { type: 'customer_action' },
    { event_id: eventId },
  );
}

export function useOpenAiPixel() {
  useEffect(() => {
    const checkAndUpdate = () => {
      if (hasMarketingConsent()) {
        loadOpenAiPixel();
      } else {
        unloadOpenAiPixel();
      }
    };

    checkAndUpdate();

    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent') {
        checkAndUpdate();
      }
    };

    const handleConsentChange = () => {
      checkAndUpdate();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);
}
